import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { projectsService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';

const ProjectEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    tech: [],
    image: '',
    status: 'Live',
    order: 0
  });

  const [newTech, setNewTech] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchProject();
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      const result = await projectsService.getById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        toast.error('Failed to load project');
      }
    } catch (error) {
      toast.error('Error loading project');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addTech = () => {
    if (newTech.trim()) {
      setFormData(prev => ({
        ...prev,
        tech: [...prev.tech, newTech.trim()]
      }));
      setNewTech('');
    }
  };

  const removeTech = (index) => {
    setFormData(prev => ({
      ...prev,
      tech: prev.tech.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.url || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      let result;
      if (isEditing) {
        result = await projectsService.update(id, formData);
      } else {
        result = await projectsService.create(formData);
      }

      if (result.success) {
        toast.success(`Project ${isEditing ? 'updated' : 'created'} successfully!`);
        setTimeout(() => {
          navigate('/adminaccess/projects');
        }, 500);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} project`);
      }
    } catch (error) {
      toast.error('Error saving project');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/projects')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Project'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">
            {isEditing ? 'Edit Project' : 'Add Project'}
          </h1>
          <p className="text-white/60">
            {isEditing ? '' : 'Add a new project to your portfolio'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Project Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., My Awesome Project"
            />
          </div>

          {/* URL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Project URL <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="url"
              value={formData.url}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., github.com/username/project"
            />
            <p className="text-white/40 text-xs mt-1">
              Enter without https:// (it will be added automatically)
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
              placeholder="Brief description of your project..."
            />
          </div>

          {/* Status - FIXED DROPDOWN */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Status <span className="text-red-400">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer
              [&>option]:bg-[#1a1a1a] [&>option]:text-white [&>option]:py-2"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' fill-opacity='0.6' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                paddingRight: '2.5rem'
              }}
            >
              <option value="Live">Live</option>
              <option value="In Progress">In Progress</option>
              <option value="Demo">Demo</option>
            </select>
          </div>

          {/* Tech Stack */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Tech Stack</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newTech}
                onChange={(e) => setNewTech(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTech();
                  }
                }}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Add technology..."
              />
              <button
                type="button"
                onClick={addTech}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tech.map((tech, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/10 text-white/80 text-sm"
                >
                  {tech}
                  <button
                    type="button"
                    onClick={() => removeTech(index)}
                    className="text-white/40 hover:text-white transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Image URL</label>
            <input
              type="url"
              name="image"
              value={formData.image}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., https://example.com/image.jpg"
            />
          </div>

          {/* Order */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Display Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="0"
            />
            <p className="text-white/40 text-xs mt-1">
              Lower numbers appear first (0 = first position)
            </p>
          </div>

          {/* Preview */}
          <div className="p-6 rounded-lg border border-white/10 bg-white/5">
            <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">Preview</h3>
            <div className="flex gap-4">
              {formData.image && (
                <img
                  src={formData.image}
                  alt={formData.title}
                  className="w-14 h-14 rounded-lg object-cover border border-white/10"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-medium">{formData.title || 'Project Title'}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      formData.status === 'Live'
                        ? 'bg-green-500/20 text-white-400'
                        : 'bg-white-500/20 text-red-400'
                    }`}
                  >
                    {formData.status}
                  </span>
                </div>
                <div className="text-white/40 text-xs mb-2">{formData.url || 'project-url.com'}</div>
                <p className="text-sm text-white/60 mb-2">
                  {formData.description || 'Project description will appear here...'}
                </p>
                {formData.tech.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {formData.tech.map((tech, index) => (
                      <span key={index} className="text-xs px-2 py-1 rounded bg-white/10 text-white/50">
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ProjectEditor;